import { plural, t } from "@lingui/core/macro";
import React, { useCallback } from "react";
import RefinedListDropdown, {
  DynamicTitleFunction
} from "../RefinementListFilter/RefinedListDropdown";
import RefinedList from "../RefinementListFilter/RefinedList";
import { FycThemedCruisesContentFragment } from "types/graphql-types";
import { DescriptionForItem } from "../RefinementListFilter/RefinedListValues";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { RefinementListRenderState } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { cruisesLabel } from "src/utils/cruise";

type Props = {
  themedCruisesContent: FycThemedCruisesContentFragment[];
};

const nameForItem =
  (content: FycThemedCruisesContentFragment[]) =>
  (items: RefinementListItem[]): RefinementListItem[] => {
    const transformedItems = [...items]
      .map((item) => {
        const theme = content?.find((s) => s.code?.trim() == item.value.trim());
        const label = theme?.name?.localized;
        if (!label) return item;

        return {
          ...item,
          label
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return transformedItems;
  };

const descriptionForItem =
  (content: FycThemedCruisesContentFragment[]) =>
  (
    item: RefinementListRenderState["items"][number]
  ): ReturnType<DescriptionForItem> => {
    const theme = content?.find((s) => s.code?.trim() == item.value.trim());
    if (!theme) return undefined;

    return theme.fycFilterDescription?.localized;
  };

const buildDynamicTitle: DynamicTitleFunction = (count) =>
  count > 0
    ? plural(count, {
        one: "# theme selected",
        other: "# themes selected"
      })
    : t`Select the features`;

export const FeatureFilterDropDown: React.FC<Props> = ({
  themedCruisesContent
}) => {
  const transformItems = useCallback(nameForItem(themedCruisesContent), [
    themedCruisesContent
  ]);

  const _descriptionForItem = useCallback(
    descriptionForItem(themedCruisesContent),
    [themedCruisesContent]
  );

  return (
    <RefinedListDropdown
      dynamicTitle={buildDynamicTitle}
      label={t`Features`}
      dataContentPiece="features_filter_interaction"
      attribute="features"
      operator="or"
      transformItems={transformItems}
      descriptionForItem={_descriptionForItem}
      limit={100}
      counterLabel={cruisesLabel}
    />
  );
};

export const FeatureFilterList: React.FC<Props> = ({
  themedCruisesContent
}) => {
  const transformItems = useCallback(nameForItem(themedCruisesContent), [
    themedCruisesContent
  ]);

  const _descriptionForItem = useCallback(
    descriptionForItem(themedCruisesContent),
    [themedCruisesContent]
  );

  return (
    <RefinedList
      title={buildDynamicTitle}
      dataContentName="features_filter_interaction"
      attribute="features"
      operator="or"
      transformItems={transformItems}
      descriptionForItem={_descriptionForItem}
      limit={100}
    />
  );
};
